"use client";

import {
  RedirectToSignIn,
  SignedIn,
  useAuth,
  useOrganizationList,
  useUser,
} from "@clerk/tanstack-react-start";
import {
  createFileRoute,
  Outlet,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import Cookies from "js-cookie";
import { Suspense, useCallback, useEffect, useRef } from "react";
import { AppSidebar } from "@/components/layout/app-sidebar";
import SkipToMain from "@/components/skip-to-main";
import { FullPageSpinner } from "@/components/ui/full-page-spinner";
import { SidebarProvider } from "@/components/ui/sidebar";
import { SearchProvider } from "@/context/search-context";
import { useOrganizationsQuery } from "@/hooks/queries/use-organizations-query";
import { cn } from "@/lib/utils";

const ROUTE_LOG_STORAGE_KEY = "lynx-route-logs";
const ADMIN_NAV_FLAG_STORAGE_KEY = "lynx-admin-navigation";
const MAX_ROUTE_LOG_ENTRIES = 20;
const RECENT_ADMIN_NAVIGATION_WINDOW_MS = 5000;

type RouteLogEntry = {
  timestamp: string;
  message: string;
  data?: unknown;
};

type AdminNavigationFlag = {
  timestamp: number;
  targetOrgId: string;
  isAdmin: boolean;
};

const appendRouteLog = (entry: RouteLogEntry) => {
  try {
    const existing = localStorage.getItem(ROUTE_LOG_STORAGE_KEY);
    const logs: RouteLogEntry[] = existing ? JSON.parse(existing) : [];
    logs.push(entry);
    while (logs.length > MAX_ROUTE_LOG_ENTRIES) {
      logs.shift();
    }
    localStorage.setItem(ROUTE_LOG_STORAGE_KEY, JSON.stringify(logs));
  } catch (error: unknown) {
    console.error("Failed to append route log", error);
  }
};

const readAdminNavigationFlag = (): AdminNavigationFlag | null => {
  try {
    const rawValue = sessionStorage.getItem(ADMIN_NAV_FLAG_STORAGE_KEY);
    return rawValue ? (JSON.parse(rawValue) as AdminNavigationFlag) : null;
  } catch (error: unknown) {
    console.error("Failed to read admin navigation flag", error);
    return null;
  }
};

const clearAdminNavigationFlag = () => {
  sessionStorage.removeItem(ADMIN_NAV_FLAG_STORAGE_KEY);
};

export const Route = createFileRoute("/_app")({
  component: AppLayoutWrapper,
});

// Wrapper component to ensure authentication before rendering the app shell
function AppLayoutWrapper() {
  const { isSignedIn, isLoaded } = useAuth();
  const { user } = useUser();

  if (!isLoaded) {
    return <FullPageSpinner />;
  }

  if (!(isSignedIn && user)) {
    // Redirect to sign in if not authenticated
    return <RedirectToSignIn />;
  }

  return <RouteComponent />;
}

function RouteComponent() {
  const defaultOpen = Cookies.get("sidebar:state") !== "false";

  // --- Tenant ID Check --- >
  const { tenantId } = useParams({ strict: false });
  const {
    data: organizationsData,
    isLoading: isOrganizationsLoading,
    isFetched: isOrganizationsFetched,
  } = useOrganizationsQuery();
  const tenants = organizationsData?.organizations ?? [];
  const hasFetchedTenants = isOrganizationsFetched && !isOrganizationsLoading;

  // Import what we need from Clerk
  const { user } = useUser();
  const { userMemberships, isLoaded: isOrgListLoaded } = useOrganizationList({
    userMemberships: {
      infinite: true,
    },
  });
  const organizations = userMemberships?.data || [];
  const isAdmin = user?.publicMetadata?.admin === true;
  const navigate = useNavigate();

  const logEvent = useCallback((message: string, data?: unknown) => {
    const entry: RouteLogEntry = {
      timestamp: new Date().toISOString(),
      message,
      data,
    };
    console.log(message, data);
    appendRouteLog(entry);
  }, []);

  const redirectToTenant = useCallback(
    (targetTenantId: string) => {
      navigate({
        to: "/$tenantId",
        params: { tenantId: targetTenantId },
        replace: true,
      });
    },
    [navigate]
  );

  const handleAdminTenantRouting = useCallback(() => {
    if (!(hasFetchedTenants || isOrgListLoaded)) {
      return;
    }

    const databaseTenantIds = hasFetchedTenants
      ? tenants.map((tenant) => tenant.id)
      : [];
    const membershipTenantIds = isOrgListLoaded
      ? organizations.map((membership) => membership.organization.id)
      : [];

    const knownTenantIds = new Set([
      ...databaseTenantIds,
      ...membershipTenantIds,
    ]);

    if (tenantId && knownTenantIds.has(tenantId)) {
      logEvent("Admin staying on requested organization", tenantId);
      return;
    }

    const fallbackTenantId =
      databaseTenantIds.at(0) ?? membershipTenantIds.at(0) ?? null;

    if (!fallbackTenantId) {
      logEvent("Admin user has no organizations available yet");
      return;
    }

    logEvent("Admin redirecting to organization", fallbackTenantId);
    redirectToTenant(fallbackTenantId);
  }, [
    hasFetchedTenants,
    isOrgListLoaded,
    logEvent,
    organizations,
    redirectToTenant,
    tenantId,
    tenants,
  ]);

  const handleMemberTenantRouting = useCallback(() => {
    if (!isOrgListLoaded) {
      return;
    }

    const membershipTenantIds = organizations.map(
      (membership) => membership.organization.id
    );

    if (membershipTenantIds.length === 0) {
      logEvent("Non-admin user has no Clerk memberships available");
      navigate({
        to: "/app/onboarding",
        replace: true,
      });
      return;
    }

    const fallbackTenantId = membershipTenantIds[0];

    if (!(tenantId && membershipTenantIds.includes(tenantId))) {
      logEvent("Redirecting member to first organization", fallbackTenantId);
      redirectToTenant(fallbackTenantId);
    }
  }, [
    isOrgListLoaded,
    logEvent,
    navigate,
    organizations,
    redirectToTenant,
    tenantId,
  ]);

  const skipDueToAdminNavigationFlag = useCallback(() => {
    const navFlag = readAdminNavigationFlag();
    logEvent("Checking admin nav flag:", navFlag);
    if (!navFlag) {
      return false;
    }

    clearAdminNavigationFlag();

    const withinWindow =
      navFlag.timestamp > Date.now() - RECENT_ADMIN_NAVIGATION_WINDOW_MS;
    const matchesTenant = navFlag.targetOrgId === tenantId;
    if (withinWindow && matchesTenant && navFlag.isAdmin) {
      logEvent("Admin navigation detected, skipping validation for:", tenantId);
      return true;
    }

    return false;
  }, [logEvent, tenantId]);

  // Refs to track component state and prevent race conditions
  const navigationInProgressRef = useRef(false);
  const mountedRef = useRef(true);

  // Cleanup effect to track mounted state
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      navigationInProgressRef.current = false;
    };
  }, []);

  useEffect(() => {
    // Guard: Don't trigger if navigation already in progress
    if (navigationInProgressRef.current) {
      logEvent("Navigation already in progress, skipping validation");
      return;
    }

    logEvent("Tenant validation check:", {
      tenantId,
      isAdmin,
      isOrganizationsLoading,
      tenantsCount: tenants.length,
      organizationsCount: organizations.length,
      databaseTenants: tenants.map((tenant) => ({
        id: tenant.id,
        name: tenant.name,
      })),
    });

    if (skipDueToAdminNavigationFlag()) {
      return;
    }

    // Set flag before performing routing logic
    navigationInProgressRef.current = true;

    try {
      if (isAdmin) {
        handleAdminTenantRouting();
      } else {
        handleMemberTenantRouting();
      }
    } finally {
      // Only reset if component still mounted
      if (mountedRef.current) {
        navigationInProgressRef.current = false;
      }
    }
  }, [
    handleAdminTenantRouting,
    handleMemberTenantRouting,
    isAdmin,
    isOrganizationsLoading,
    logEvent,
    organizations,
    skipDueToAdminNavigationFlag,
    tenantId,
    tenants,
  ]);

  // < --- Tenant ID Check ---

  // --- Original Render Logic --- >
  // If tenantId is missing or invalid, the redirect above will happen.
  // If tenantId IS present, render the layout.
  // Show loading spinner while we're waiting for organizations to load or redirecting
  if (!hasFetchedTenants) {
    return <FullPageSpinner />;
  }

  return (
    <SignedIn>
      <SearchProvider>
        <SidebarProvider defaultOpen={defaultOpen}>
          <SkipToMain />
          <AppSidebar />
          <div
            className={cn(
              "ml-auto flex h-screen w-full max-w-full flex-col",
              "peer-data-[state=collapsed]:w-[calc(100%_-_var(--sidebar-width-icon)_-_theme(spacing.4))]",
              "peer-data-[state=expanded]:w-[calc(100%_-_var(--sidebar-width)_-_theme(spacing.4))]",
              "transition-[width] duration-200 ease-linear"
            )}
            id="content"
          >
            <main className="flex-1 overflow-y-auto">
              <div className="flex-1 space-y-4 p-4 pt-4 md:p-8">
                <Suspense fallback={<FullPageSpinner />}>
                  <Outlet />
                </Suspense>
              </div>
            </main>
          </div>
        </SidebarProvider>
      </SearchProvider>
    </SignedIn>
  );
}
